import { useEffect, useState } from "react";
import { KeyRound, Pencil, Plus, RotateCcw, Save, UserCheck, UserX } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Panel, Tone } from "@/components/shared";
import { PermissionList } from "@/components/PermissionList";
import { AddStaffDialog, EditStaffDialog, ResetPasswordDialog } from "@/components/StaffDialogs";
import { useAuth } from "@/lib/auth";
import { api, errMsg, fdt, label, useApi } from "@/lib/api";

function StaffTab({ staff, setStaff, roles, catalog, reload }) {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const toggleActive = (s) => {
    const flip = (active) => setStaff((list) => list.map((x) => (x.id === s.id ? { ...x, active } : x)));
    flip(!s.active);
    api.patch(`/admin/users/${s.id}`, { active: !s.active })
      .then(() => toast.success(`${s.name} ${s.active ? "deactivated" : "reactivated"}`))
      .catch((e) => { flip(s.active); toast.error(errMsg(e)); });
  };
  const rows = staff.filter((s) => `${s.name} ${s.email} ${s.role}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between gap-2 mb-4">
        <Input className="w-full sm:w-72" placeholder="Search staff" value={q} onChange={(e) => setQ(e.target.value)} data-testid="staff-search-input" />
        <Button onClick={() => setAdding(true)} data-testid="add-staff-button"><Plus className="h-4 w-4 mr-2" />Add staff</Button>
      </div>
      <div className="rounded-lg border bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="staff-table">
          <thead><tr className="border-b bg-muted/40 text-xs text-muted-foreground">
            {["Name", "Role", "Status", "Overrides", "Last login", ""].map((h) => <th key={h} className="text-left font-medium px-4 py-2.5">{h}</th>)}
          </tr></thead>
          <tbody>{rows.map((s) => {
            const ov = s.permOverrides.grant.length + s.permOverrides.revoke.length;
            return (
              <tr key={s.id} className={`border-b last:border-0 ${s.active ? "" : "opacity-60"}`} data-testid={`staff-row-${s.email}`}>
                <td className="px-4 py-3"><div className="font-medium">{s.name}{s.id === user.id && <span className="text-xs text-muted-foreground"> (you)</span>}</div><div className="text-xs text-muted-foreground">{s.email}</div></td>
                <td className="px-4 py-3"><Tone tone={s.role === "SUPER_ADMIN" ? "indigo" : "gray"}>{label(s.role)}</Tone></td>
                <td className="px-4 py-3 space-x-1">{s.active ? <Tone tone="green">Active</Tone> : <Tone tone="red">Deactivated</Tone>}
                  {s.mustChangePassword && <Tone tone="amber">Temp password</Tone>}</td>
                <td className="px-4 py-3">{ov ? <Tone tone="purple">+{s.permOverrides.grant.length} / −{s.permOverrides.revoke.length}</Tone> : <span className="text-xs text-muted-foreground">Role default</span>}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{s.lastLoginAt ? fdt(s.lastLoginAt) : "Never"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(s)} data-testid={`edit-staff-${s.email}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => setResetting(s)} data-testid={`reset-staff-${s.email}`} title="Reset password"><KeyRound className="h-3.5 w-3.5" /></Button>
                  {s.id !== user.id && (
                    <Button size="sm" variant="ghost" onClick={() => toggleActive(s)} data-testid={`toggle-active-${s.email}`} title={s.active ? "Deactivate" : "Reactivate"}>
                      {s.active ? <UserX className="h-3.5 w-3.5 text-rose-600" /> : <UserCheck className="h-3.5 w-3.5 text-emerald-600" />}
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
      <AddStaffDialog open={adding} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); reload(); }} />
      <EditStaffDialog staff={editing} roles={roles} catalog={catalog} me={user} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
      <ResetPasswordDialog staff={resetting} onClose={() => { setResetting(null); reload(); }} />
    </>
  );
}

function RolesTab({ roles, catalog, reload }) {
  const [sel, setSel] = useState("SALES");
  const role = roles.find((r) => r.role === sel);
  const [perms, setPerms] = useState([]);
  useEffect(() => { if (role) setPerms(role.permissions); }, [role]);
  if (!role) return null;
  const dirty = [...perms].sort().join() !== [...role.permissions].sort().join();
  const save = async () => {
    try { await api.put(`/admin/roles/${sel}`, { permissions: perms }); toast.success(`${label(sel)} permissions saved`); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid lg:grid-cols-4 gap-6">
      <div className="space-y-1.5" data-testid="role-list">
        {roles.map((r) => (
          <button key={r.role} onClick={() => setSel(r.role)} data-testid={`role-item-${r.role}`}
            className={`w-full text-left rounded-md border px-3 py-2.5 text-sm transition-colors ${sel === r.role ? "bg-secondary/60 border-foreground/25" : "hover:bg-secondary/30"}`}>
            <div className="font-medium">{label(r.role)}</div>
            <div className="text-xs text-muted-foreground">{r.permissions.length} permissions · {r.userCount} staff</div>
          </button>
        ))}
      </div>
      <Panel className="lg:col-span-3" title={`${label(sel)} permissions`}
        action={role.editable && <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => setPerms(role.defaults)} data-testid="role-reset-defaults"><RotateCcw className="h-3.5 w-3.5 mr-1.5" />Defaults</Button>
          <Button size="sm" onClick={save} disabled={!dirty} data-testid="role-save-button"><Save className="h-3.5 w-3.5 mr-1.5" />Save</Button></div>}>
        <div className="p-5">
          {!role.editable && <div className="mb-4 text-xs text-muted-foreground">Super Admin always has every permission and can't be restricted.</div>}
          <PermissionList catalog={catalog} disabled={!role.editable} testid="role-perm" isOn={(a) => perms.includes(a)}
            onToggle={(a, v) => setPerms(v ? [...perms, a] : perms.filter((x) => x !== a))} />
        </div>
      </Panel>
    </div>
  );
}

function ActivityTab() {
  const { data } = useApi("/admin/audit");
  return (
    <div className="rounded-lg border bg-card divide-y" data-testid="role-activity-list">
      {data?.map((a) => (
        <div key={a.id} className="flex gap-3 px-5 py-3 text-sm">
          <div className="flex-1"><span className="font-medium">{a.userName}</span> · {a.action}
            {Object.keys(a.details || {}).length > 0 && <div className="text-xs text-muted-foreground mt-0.5">{Object.entries(a.details).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") || "—" : v}`).join(" · ")}</div>}</div>
          <div className="text-xs text-muted-foreground font-mono whitespace-nowrap">{fdt(a.createdAt)}</div>
        </div>
      ))}
      {data?.length === 0 && <div className="py-10 text-center text-sm text-muted-foreground">No activity yet</div>}
    </div>
  );
}

export default function RoleManager() {
  const { data: staff, reload: reloadStaff, setData: setStaff } = useApi("/admin/users");
  const { data: rc, reload: reloadRoles } = useApi("/admin/roles");
  const reload = () => { reloadStaff(); reloadRoles(); };
  if (!staff || !rc) return null;
  return (
    <div>
      <PageHeader title="Role Manager" subtitle="Add staff, assign roles and fine-tune what each person can do." />
      <Tabs defaultValue="staff">
        <TabsList className="mb-5">
          <TabsTrigger value="staff" data-testid="tab-staff">Staff ({staff.length})</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles">Role permissions</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
        </TabsList>
        <TabsContent value="staff"><StaffTab staff={staff} setStaff={setStaff} roles={rc.roles} catalog={rc.catalog} reload={reload} /></TabsContent>
        <TabsContent value="roles"><RolesTab roles={rc.roles} catalog={rc.catalog} reload={reload} /></TabsContent>
        <TabsContent value="activity"><ActivityTab /></TabsContent>
      </Tabs>
    </div>
  );
}
